import type React from "react";
import { useThemeToken } from "@/styles/tokens/useThemeTokens";
import { cx } from "@/utils/cx";

type SidebarStyleOptions = {
	collapsed?: boolean;
};

export const sidebarStyles = (options?: SidebarStyleOptions) => {
	const { getColors, getSpacing, getRadius } = useThemeToken();
	const colors = getColors;
	const spacing = getSpacing;
	const radius = getRadius;

	const collapsed = Boolean(options?.collapsed);

	const desktop = {
		aside: cx(
			"flex h-full flex-col justify-between overflow-hidden transition-all duration-300 md:border-r",
			collapsed ? "w-full md:w-16" : "w-full md:w-[296px]"
		),
		footer: cx(
			"mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6",
			collapsed && "pointer-events-none opacity-0"
		),
		profileCard:
			"relative flex text-start items-center gap-3 ring-1 ring-inset outline-0",
		navList: "!lg:px-2 !px-2",
		toggleButton: cx(
			"flex cursor-pointer items-center outline-brand",
			collapsed ? "w-full justify-end" : "justify-center"
		),
		logo: collapsed ? "hidden" : "",
	};

	const desktopStyle: Record<string, React.CSSProperties> = {
		aside: {
			backgroundColor: colors.bg_primary,
			borderRight: `1px solid ${colors.border_secondary}`,
			paddingTop: spacing.spacing_xl,
		},
		contentWrapper: {
			display: "flex",
			flexDirection: "column",
			gap: spacing.spacing_lg,
			paddingInline: spacing.spacing_xl,
		},
		headerRow: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
		},
		toggleButton: {
			padding: spacing.spacing_md,
			outlineColor: colors.border_brand,
			color: colors.text_quaternary,
			borderRadius: radius.radius_md,
		},
		profileCard: {
			borderRadius: radius.radius_xl,
			padding: spacing.spacing_lg,
			boxShadow: `inset 0 0 0 1px ${colors.border_secondary}`,
		},
	};

	const mobile = {
		header: "flex h-16 items-center justify-between border-b lg:hidden",
		openButton: "group flex items-center justify-center outline-brand",
		overlay: "fixed inset-0 z-50 pr-16 lg:hidden",
		closeButton:
			"absolute top-3 -right-14 flex cursor-pointer items-center justify-center outline-brand",
		modal: "relative w-full h-full cursor-auto will-change-transform",
		dialog: "w-full h-[100dvh] outline-none focus:outline-none",
	};

	const mobileStyle: Record<string, React.CSSProperties> = {
		header: {
			backgroundColor: colors.bg_primary,
			borderBottom: `1px solid ${colors.border_secondary}`,
			paddingBlock: spacing.spacing_lg,
			paddingInline: spacing.spacing_xl,
		},
		openButton: {
			padding: spacing.spacing_md,
			outlineColor: colors.border_brand,
			backgroundColor: colors.bg_primary,
			color: colors.text_secondary,
			borderRadius: radius.radius_lg,
		},
		overlay: {
			backgroundColor: "transparent",
			backdropFilter: "blur(8px)",
			WebkitBackdropFilter: "blur(8px)",
		},
		closeButton: {
			padding: spacing.spacing_md,
			outlineColor: colors.border_brand,
			color: colors.text_secondary,
			opacity: 0.7,
			borderRadius: radius.radius_lg,
		},
	};

	return {
		collapsed,
		desktop,
		desktopStyle,
		mobile,
		mobileStyle,
	};
};
